import * as Yup from 'yup';

import { Button, Col, Form, InputGroup, Row } from 'react-bootstrap';
import { Field, Formik } from 'formik';
import { NetZeroChart, NetZeroHeader } from '../../ui';
import React, { useEffect, useRef, useState } from 'react';
import {
  electricityHardLimit,
  energyUnits,
  getBreakdownSteps,
  getHeatingMethodName,
  getHeatingUnitOfMeasure,
  getInteractiveBreakdown,
  getTotalMileage,
  kwhFuelHardLimit,
  litreFuelHardLimit,
  mileageHardLimit,
} from '../../utils/netZeroHelper';
import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { light, off, white } from '../../theme/colors';

import ErrorLabel from '../../ui/ErrorLabel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import NetZeroEmailModal from '../../ui/Modal/NetZeroEmailModal';
import PropTypes from 'prop-types';
import SolarIcon from '../../assets/svg/solar-icon.svg';
import SolarSidebar from '../../assets/images/solar-sidebar.png';
import cx from 'classnames';
import netZeroContent from '../../utils/netZeroContent';
import styled from 'styled-components';
import { useNavigate } from 'react-router';

const CustomCardWrapper = styled.div`
  display: flex;
  flex-direction: row;
  background: linear-gradient(180deg, #f8af1f, #f7d22d);
  margin-top: 30px;
  border-radius: 24px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.3);

  @media screen and (max-width: 780px) {
    margin-top: 50px;
  }

  @media screen and (max-width: 960px) {
    flex-direction: column;
  }
`;

const CustomCard = styled.div`
  background-color: #fff;
  border-radius: 24px;
  display: flex;
  flex-direction: column;
  max-width: 720px;

  @media screen and (max-width: 960px) {
    padding-bottom: 20px;
  }
`;

const BreakdownWrapper = styled.div`
  margin: 0;
  width: 100%;
`;

const CustomCardFooter = styled.div`
  border-bottom-left-radius: 24px;
  border-bottom-right-radius: 24px;
  padding: 30px 50px 8px;
  font-weight: 500;
  color: black;
  font-size: 22px;
  text-align: left;

  .row {
    padding-left: 10px;
    padding-right: 10px;

    &:first-child {
      & > div {
        border-top: 1px solid ${light};
        margin-bottom: 20px;
        padding-top: 24px;
      }

      @media (max-width: 768px) {
        &:first-child {
          border-top: 1px solid ${light} !important;
          padding-top: 15px;
        }
      }
    }

    & > div {
      padding-top: 0;
      padding-left: 8px;
      padding-right: 8px;

      &:first-child {
        padding-left: 0;
      }

      &:last-child {
        padding-right: 0;
      }

      @media (max-width: 768px) {
        border-top: none !important;
        padding-left: 15px !important;
        padding-right: 15px !important;
        padding-top: 0 !important;
      }

      .input-group {
        .form-control {
          &.with-unit-of-measure {
            border-right: none;
          }
        }

        .input-group-text {
          border-top-left-radius: 0;
          border-bottom-left-radius: 0;
          border-left: none;
          border-color: ${light};
          background: none;
          padding: 0;

          .unit-wrapper {
            margin: 4px;
            padding: 2px 7px;
            border-left: 1px solid ${light};
          }

          &.disabled {
            background-color: #e9ecef;
          }
        }
      }

      .form-check {
        padding: 0;
        margin-bottom: 15px;

        .form-check-input {
          display: none;
        }

        .form-check-label {
          font-size: 14px;
          font-weight: normal;
          padding: 10px 0 10px 82px;
          cursor: pointer;
          white-space: nowrap;
        }

        &.disabled {
          cursor: not-allowed;
          opacity: 0.7;

          .form-check-label {
            cursor: not-allowed;
          }
        }
      }
    }
  }

  @media screen and (max-width: 780px) {
    font-size: 14px;
    font-weight: 600;
    padding: 20px 25px;
  }
`;

const CheckboxBackground = styled.div`
  position: absolute;
  height: 40px;
  width: 74px;
  border-radius: 22px;

  .checkbox-indicator {
    position: absolute;
    background: ${white};
    width: 32px;
    height: 32px;
    border-radius: 50%;
    margin: 4px;
    transition: left 0.3s;
    cursor: pointer;
  }

  .checkbox-icons {
    height: 38px;
    padding: 2px;
    display: flex;
    align-items: center;
    justify-content: space-around;
  }

  &.unchecked {
    background: ${light};

    .checkbox-indicator {
      left: 0;
    }
  }

  &.checked {
    .checkbox-indicator {
      left: 34px;
    }
  }

  &.disabled {
    cursor: not-allowed;
    opacity: 0.75;
  }
`;

const HeaderWrapper = styled.div`
  max-width: 720px;
  position: relative;
  top: -25px;

  @media screen and (max-width: 780px) {
    top: -50px;
  }

  @media screen and (min-width: 961px) {
    .section-wrapper {
      .info-wrapper {
        .info.title {
          border-radius: 20px;
        }
      }
    }
  }
`;

const SolarPanel = styled.div`
  max-width: 250px;
  color: ${white};
  padding: 0 20px 20px 20px;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative;

  @media screen and (max-width: 960px) {
    width: auto;
    max-width: 720px;
  }

  .icon-wrapper {
    height: 50px;

    .solar-icon {
      width: 70px;
      height: 70px;
      margin: 0 auto;
      position: relative;
      top: -35px;
    }
  }

  h3 {
    color: ${white};
    font-size: 20px;
    line-height: 1.45;
    max-width: 400px;
    margin-left: auto;
    margin-right: auto;
  }

  p {
    font-size: 15px;
    line-height: 1.5;
    max-width: 400px;
    margin-left: auto;
    margin-right: auto;
  }

  .app-screen {
    width: 90%;
    margin: 0 auto;
    max-width: 220px;
  }

  button {
    max-width: 400px;
    margin-left: auto;
    margin-right: auto;
  }

  &:after {
    content: '';
    position: absolute;
    border-style: solid;
    border-width: 25px 25px 25px 0;
    border-color: transparent #f8bf24;
    display: block;
    width: 0;
    z-index: 1;
    left: -25px;
    top: 42%;

    @media screen and (max-width: 960px) {
      display: none;
    }
  }
`;

const CheckboxIndicator = ({ className }) => (
  <CheckboxBackground className={className}>
    <div className="checkbox-indicator" />
    <div className="checkbox-icons">
      <FontAwesomeIcon color={white} icon={faCheck} size="xs" />
      <FontAwesomeIcon color={off} icon={faTimes} size="xs" />
    </div>
  </CheckboxBackground>
);

CheckboxIndicator.propTypes = {
  className: PropTypes.string,
};

const InteractiveContainer = ({
  heatingMethod,
  usage,
  cars,
  setShowGetStarted,
}) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [breakdownSteps, setBreakdownSteps] = useState(undefined);
  const [emissions, setEmissions] = useState(undefined);
  const [prevEmissions, setPrevEmissions] = useState(0);
  const [duration, setDuration] = useState(3);
  const [useLowCarbonHeat, setUseLowCarbonHeat] = useState(true);
  const [useElectricVehicle, setUseElectricVehicle] = useState(true);
  const [useSolarArray, setUseSolarArray] = useState(true);
  const [interactiveBreakdown, setInteractiveBreakdown] = useState(undefined);
  const [showEmailModal, setShowEmailModal] = useState(true);
  const [hasCars, setHasCars] = useState(undefined);
  const electricityUsageRef = useRef(null);
  const heatingUsageRef = useRef(null);
  const annualMileageRef = useRef(null);
  const useLowCarbonHeatRef = useRef(null);
  const useElectricVehicleRef = useRef(null);
  const useSolarArrayRef = useRef(null);

  const electricityMessage =
    'Please enter your annual electricity usage in kWh';
  const heatingUnitOfMeasure = getHeatingUnitOfMeasure(heatingMethod);
  const heatingMessage = `Please enter your annual ${getHeatingMethodName(
    heatingMethod,
  ).toLowerCase()} usage in ${heatingUnitOfMeasure}`;
  const heatingHardLimit =
    heatingUnitOfMeasure === energyUnits.KWH
      ? kwhFuelHardLimit
      : litreFuelHardLimit;

  const shouldDisableHeatingInput = heatingMethod => {
    return (
      heatingMethod === 'heat-pump' || heatingMethod === 'electric-radiators'
    );
  };

  const onUpdateInteractive = () => {
    const electricityUsage = electricityUsageRef?.current?.value;
    const heatingUsage = heatingUsageRef?.current?.value;
    const annualMileage = annualMileageRef?.current?.value;

    if (
      electricityUsage <= electricityHardLimit &&
      heatingUsage <= heatingHardLimit &&
      annualMileage <= mileageHardLimit
    ) {
      const interactiveBreakdown = getInteractiveBreakdown(
        heatingMethod,
        {
          electricityUsage: electricityUsage,
          heatingUsage: heatingUsage,
        },
        cars,
        annualMileage,
        useLowCarbonHeatRef?.current?.checked,
        useSolarArrayRef?.current?.checked,
        useElectricVehicleRef?.current?.checked,
      );
      setDuration(1);
      setPrevEmissions(emissions);
      setEmissions(interactiveBreakdown.total);
      setInteractiveBreakdown(interactiveBreakdown);
    }
  };

  useEffect(() => {
    const steps = getBreakdownSteps(heatingMethod, usage, cars);
    setHasCars(!!cars.length);
    setUseElectricVehicle(!!cars.length);
    setUseLowCarbonHeat(!shouldDisableHeatingInput(heatingMethod));
    setBreakdownSteps(steps);
    setEmissions(steps[6].total);
    setShowGetStarted(true);
    setIsLoading(false);
  }, [heatingMethod, cars, usage, setShowGetStarted]);

  return (
    !isLoading && (
      <>
        <NetZeroEmailModal
          onClose={() => setShowEmailModal(false)}
          show={showEmailModal}
        />
        <CustomCardWrapper>
          <CustomCard>
            <BreakdownWrapper>
              <HeaderWrapper>
                <NetZeroHeader
                  prevEmissions={prevEmissions}
                  emissions={emissions}
                  duration={duration}
                  currentContent={netZeroContent.GRID}
                  interactive
                />
              </HeaderWrapper>
              <NetZeroChart
                currentStep={1}
                breakdownSteps={breakdownSteps}
                heatingMethod={heatingMethod}
                interactiveBreakdown={interactiveBreakdown}
                interactive
                hideNotations
              />
            </BreakdownWrapper>
            <Formik
              initialValues={{
                electricityUsage: usage.electricityUsage,
                heatingUsage: usage.heatingUsage,
                annualMileage: getTotalMileage(cars),
              }}
              validationSchema={Yup.object().shape({
                electricityUsage: Yup.number()
                  .min(0, electricityMessage)
                  .max(
                    electricityHardLimit,
                    'The value you’ve entered is very high compared to the average home',
                  )
                  .required(electricityMessage),
                heatingUsage: Yup.number()
                  .min(0, heatingMessage)
                  .max(
                    heatingHardLimit,
                    'The value you’ve entered is very high compared to the average home',
                  )
                  .required(heatingMessage),
                annualMileage: Yup.number()
                  .min(0, 'Please enter your annual mileage')
                  .max(
                    mileageHardLimit,
                    "The value you've entered is very high compared to the average driver",
                  )
                  .required('Please enter your annual mileage'),
              })}>
              {() => (
                <CustomCardFooter>
                  <Row>
                    <Col sm={12} md={4}>
                      <Form.Label>
                        Annual electricity
                        <br />
                        usage
                      </Form.Label>
                      <Field name="electricityUsage">
                        {({ field, meta }) => (
                          <Form.Group controlId="electricityUsage">
                            <InputGroup>
                              <Form.Control
                                type="number"
                                ref={electricityUsageRef}
                                value={field.value}
                                step="100"
                                min="0"
                                onChange={e => {
                                  field.onChange(e);
                                  onUpdateInteractive();
                                }}
                                onBlur={e => {
                                  field.onBlur(e);
                                  onUpdateInteractive();
                                }}
                                isValid={!meta.error && !!field.value}
                                placeholder={'Electricity usage'}
                                className="with-unit-of-measure"
                              />
                              <InputGroup.Text>
                                <div className="unit-wrapper">kWh</div>
                              </InputGroup.Text>
                            </InputGroup>
                            {meta.error && meta.touched && (
                              <ErrorLabel label={meta.error} />
                            )}
                          </Form.Group>
                        )}
                      </Field>
                    </Col>
                    <Col sm={12} md={4}>
                      <Form.Label>
                        Annual energy for
                        <br />
                        heating
                      </Form.Label>
                      <Field name="heatingUsage">
                        {({ field, meta }) => (
                          <Form.Group controlId="heatingUsage">
                            <InputGroup>
                              <Form.Control
                                ref={heatingUsageRef}
                                type="number"
                                value={field.value}
                                step="100"
                                min="0"
                                onChange={e => {
                                  field.onChange(e);
                                  onUpdateInteractive();
                                }}
                                onBlur={e => {
                                  field.onBlur(e);
                                  onUpdateInteractive();
                                }}
                                isValid={!meta.error && !!field.value}
                                placeholder={'Heating energy'}
                                disabled={shouldDisableHeatingInput(
                                  heatingMethod,
                                )}
                                className="with-unit-of-measure"
                              />
                              <InputGroup.Text
                                className={cx({
                                  disabled:
                                    shouldDisableHeatingInput(heatingMethod),
                                })}>
                                <div className="unit-wrapper">
                                  {getHeatingUnitOfMeasure(heatingMethod)}
                                </div>
                              </InputGroup.Text>
                            </InputGroup>
                            {meta.error && meta.touched && (
                              <ErrorLabel label={meta.error} />
                            )}
                          </Form.Group>
                        )}
                      </Field>
                    </Col>
                    <Col sm={12} md={4}>
                      <Form.Label>
                        Annual total
                        <br />
                        mileage
                      </Form.Label>
                      <Field name="annualMileage">
                        {({ field, meta }) => (
                          <Form.Group controlId="annualMileage">
                            <InputGroup>
                              <Form.Control
                                type="number"
                                ref={annualMileageRef}
                                value={field.value}
                                step="1000"
                                min="0"
                                onChange={e => {
                                  field.onChange(e);
                                  onUpdateInteractive();
                                }}
                                onBlur={e => {
                                  field.onBlur(e);
                                  onUpdateInteractive();
                                }}
                                isValid={!meta.error && !!field.value}
                                disabled={!hasCars}
                                placeholder={'Annual mileage'}
                              />
                            </InputGroup>
                            {meta.error && meta.touched && (
                              <ErrorLabel label={meta.error} />
                            )}
                          </Form.Group>
                        )}
                      </Field>
                    </Col>
                  </Row>
                  <Row>
                    <Col sm={12} md={4}>
                      <CheckboxIndicator
                        className={cx({
                          checked: useLowCarbonHeat,
                          unchecked: !useLowCarbonHeat,
                          disabled: shouldDisableHeatingInput(heatingMethod),
                          'background-heating-gradient': true,
                        })}
                      />
                      <Form.Check
                        type="checkbox"
                        name="useLowCarbonHeat"
                        id="low-carbon-heat"
                        ref={useLowCarbonHeatRef}
                        checked={useLowCarbonHeat}
                        onChange={({ target }) => {
                          setUseLowCarbonHeat(target.checked);
                          onUpdateInteractive();
                        }}
                        disabled={shouldDisableHeatingInput(heatingMethod)}
                        label="Low carbon heat"
                        className={cx({
                          checked: useLowCarbonHeat,
                          disabled: shouldDisableHeatingInput(heatingMethod),
                        })}
                      />
                    </Col>
                    <Col sm={12} md={4}>
                      <CheckboxIndicator
                        className={cx({
                          checked: useElectricVehicle,
                          unchecked: !useElectricVehicle,
                          disabled: !hasCars,
                          'background-car-gradient': true,
                        })}
                      />
                      <Form.Check
                        type="checkbox"
                        name="useElectricVehicle"
                        id="electric-vehicle"
                        ref={useElectricVehicleRef}
                        checked={useElectricVehicle}
                        onChange={({ target }) => {
                          setUseElectricVehicle(target.checked);
                          onUpdateInteractive();
                        }}
                        disabled={!hasCars}
                        label="Electric vehicle"
                        className={cx({
                          checked: useElectricVehicle,
                          disabled: !hasCars,
                        })}
                      />
                    </Col>
                    <Col sm={12} md={4}>
                      <CheckboxIndicator
                        className={cx({
                          checked: useSolarArray,
                          unchecked: !useSolarArray,
                          'background-solar-gradient': true,
                        })}
                      />
                      <Form.Check
                        type="checkbox"
                        name="useSolarArray"
                        id="solar-array"
                        ref={useSolarArrayRef}
                        checked={useSolarArray}
                        onChange={({ target }) => {
                          setUseSolarArray(target.checked);
                          onUpdateInteractive();
                        }}
                        label="Solar array"
                        className={cx({ checked: useSolarArray })}
                      />
                    </Col>
                  </Row>
                </CustomCardFooter>
              )}
            </Formik>
          </CustomCard>
          <SolarPanel>
            <div className="icon-wrapper">
              <img className="solar-icon" alt="Solar Icon" src={SolarIcon} />
            </div>
            <h3>
              Link Loop to your smart meter for FREE, and get started on your
              solar journey!
            </h3>
            <p>
              Using <strong>personalised data</strong> from your household’s
              energy usage, Loop will take you to net-zero.
            </p>
            <img
              src={SolarSidebar}
              alt="App Screen Icon"
              className="app-screen"
            />
            <div>
              <Button
                onClick={() => navigate('/signup')}
                variant="primary"
                className="mb-0 w-100">
                Get Started
              </Button>
            </div>
          </SolarPanel>
        </CustomCardWrapper>
      </>
    )
  );
};

InteractiveContainer.propTypes = {
  heatingMethod: PropTypes.string,
  usage: PropTypes.shape({
    electricityUsage: PropTypes.number,
    heatingUsage: PropTypes.number,
  }),
  cars: PropTypes.array,
  setShowGetStarted: PropTypes.func,
};

export default InteractiveContainer;
